import React, { useState } from 'react';
import {
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Box,
  InputAdornment,
  Paper
} from '@mui/material';
import Grid from '@mui/material/Grid';
import SaveIcon from '@mui/icons-material/Save';
import ColorLensIcon from '@mui/icons-material/ColorLens';
import RestoreIcon from '@mui/icons-material/Restore';
import { useTranslation } from 'react-i18next';

interface BrandingSettingsProps {
  branding: any;
  onUpdate: (branding: any) => Promise<void>;
}

export default function BrandingSettings({ branding, onUpdate }: BrandingSettingsProps) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    primary_color: branding.primary_color || '#1976d2',
    secondary_color: branding.secondary_color || '#dc004e',
    logo_url: branding.logo_url || '',
    favicon_url: branding.favicon_url || '',
    login_background_url: branding.login_background_url || '',
    custom_css: branding.custom_css || ''
  });
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const handleChange = (field: string, value: string) => {
    setFormData({
      ...formData,
      [field]: value
    });
    setHasChanges(true);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onUpdate(formData);
      setHasChanges(false);
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setFormData({
      primary_color: '#1976d2',
      secondary_color: '#dc004e',
      logo_url: '',
      favicon_url: '',
      login_background_url: '',
      custom_css: ''
    });
    setHasChanges(true);
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {t('system.branding.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('system.branding.description')}
        </Typography>

        <Grid container spacing={3}>
          {/* Color Settings */}
          <Grid size={12}>
            <Typography variant="subtitle1" gutterBottom>
              {t('system.branding.colors')}
            </Typography>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={t('system.branding.primaryColor')}
              value={formData.primary_color}
              onChange={(e) => handleChange('primary_color', e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <input
                        type="color"
                        value={formData.primary_color}
                        onChange={(e) => handleChange('primary_color', e.target.value)}
                        style={{
                          width: 30,
                          height: 30,
                          border: 'none',
                          borderRadius: 4,
                          cursor: 'pointer'
                        }}
                      />
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={t('system.branding.secondaryColor')}
              value={formData.secondary_color}
              onChange={(e) => handleChange('secondary_color', e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <input
                        type="color"
                        value={formData.secondary_color}
                        onChange={(e) => handleChange('secondary_color', e.target.value)}
                        style={{
                          width: 30,
                          height: 30,
                          border: 'none',
                          borderRadius: 4,
                          cursor: 'pointer'
                        }}
                      />
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Grid>

          {/* Image URLs */}
          <Grid size={12}>
            <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
              {t('system.branding.images')}
            </Typography>
          </Grid>

          <Grid size={12}>
            <TextField
              fullWidth
              label={t('system.branding.logoUrl')}
              value={formData.logo_url}
              onChange={(e) => handleChange('logo_url', e.target.value)}
              placeholder="https://example.com/logo.png"
              helperText={t('system.branding.logoUrlHelp')}
            />
          </Grid>

          <Grid size={12}>
            <TextField
              fullWidth
              label={t('system.branding.faviconUrl')}
              value={formData.favicon_url}
              onChange={(e) => handleChange('favicon_url', e.target.value)}
              placeholder="https://example.com/favicon.ico"
              helperText={t('system.branding.faviconUrlHelp')}
            />
          </Grid>

          <Grid size={12}>
            <TextField
              fullWidth
              label={t('system.branding.loginBackgroundUrl')}
              value={formData.login_background_url}
              onChange={(e) => handleChange('login_background_url', e.target.value)}
              placeholder="https://example.com/background.jpg"
              helperText={t('system.branding.loginBackgroundHelp')}
            />
          </Grid>

          {/* Custom CSS */}
          <Grid size={12}>
            <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
              {t('system.branding.advanced')}
            </Typography>
          </Grid>

          <Grid size={12}>
            <TextField
              fullWidth
              label={t('system.branding.customCss')}
              value={formData.custom_css}
              onChange={(e) => handleChange('custom_css', e.target.value)}
              multiline
              rows={4}
              placeholder="/* Custom CSS rules */"
              helperText={t('system.branding.customCssHelp')}
              sx={{ fontFamily: 'monospace' }}
            />
          </Grid>

          {/* Preview */}
          <Grid size={12}>
            <Paper 
              elevation={1} 
              sx={{ 
                p: 3, 
                mt: 2,
                backgroundColor: 'grey.50',
                border: '1px solid',
                borderColor: 'grey.300'
              }}
            >
              <Typography variant="subtitle2" gutterBottom>
                {t('system.branding.preview')}
              </Typography>
              <Box 
                sx={{ 
                  display: 'flex', 
                  gap: 2, 
                  alignItems: 'center',
                  mt: 2 
                }}
              >
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    backgroundColor: formData.primary_color,
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <ColorLensIcon sx={{ color: 'white' }} />
                </Box>
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    backgroundColor: formData.secondary_color,
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <ColorLensIcon sx={{ color: 'white' }} />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {t('system.branding.previewText')}
                </Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'space-between' }}>
          <Button
            startIcon={<RestoreIcon />}
            onClick={handleReset}
          >
            {t('system.branding.resetDefaults')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={!hasChanges || isSaving}
          >
            {t('common.save')}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}